/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import React from 'react';
import { ArrowDropDownDown } from '@opengeoweb/theme';
import type { BatchSelectionState } from './selectAllButtonUtils';
import { useWarningsTranslation } from '../../../utils/i18n';

export interface SelectAllButtonProps {
  selectionState: BatchSelectionState;
  onSelectAll: () => void;
  onSelectNone: () => void;
  disabled?: boolean;
}

export const SelectAllButton: React.FC<SelectAllButtonProps> = ({
  selectionState,
  onSelectAll,
  onSelectNone,
  disabled = false,
}) => {
  const { t } = useWarningsTranslation();
  const allLabel = t('filter-all');
  const menuTooltipTitle = t('filter-select');
  const noneLabel = t('filter-none');
  const selectAllLabel = t('filter-select-all');
  const deselectAllLabel = t('filter-deselect-all');
  const tooltipTitle =
    selectionState === 'all' || selectionState === 'some'
      ? deselectAllLabel
      : selectAllLabel;

  const id = React.useId();
  const menuButtonId = `${id}-menu-button`;
  const menuId = `${id}-menu`;
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );

  const menuOpen = Boolean(menuAnchorEl);
  const isActive = !disabled && (menuOpen || selectionState !== 'none');

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (disabled) {
      return;
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setMenuAnchorEl(null);
  };

  const toggleSelection = (): void => {
    if (disabled) {
      return;
    }
    if (selectionState === 'none') {
      onSelectAll();
      return;
    }
    onSelectNone();
  };

  const handleCheckboxClick = (
    event: React.MouseEvent<HTMLSpanElement>,
  ): void => {
    event.stopPropagation();
    toggleSelection();
  };

  const handleCheckboxKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>,
  ): void => {
    if (disabled || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    event.preventDefault();
    toggleSelection();
  };

  const getSegmentHoverBackground = (theme: Theme): string => {
    if (disabled) {
      return 'transparent';
    }
    return isActive ? theme.palette.primary.dark : theme.palette.action.hover;
  };

  const segmentHoverSx = {
    borderRadius: 0.5,
    '&:hover': {
      backgroundColor: getSegmentHoverBackground,
    },
  };

  const iconColor = isActive ? 'common.white' : 'text.primary';
  const ariaChecked =
    selectionState === 'some' ? 'mixed' : selectionState === 'all';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 0.5,
        backgroundColor: isActive ? 'primary.main' : 'transparent',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Tooltip placement="bottom" title={menuTooltipTitle}>
        <span>
          <IconButton
            aria-controls={menuOpen ? menuId : undefined}
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-label={menuTooltipTitle}
            disabled={disabled}
            id={menuButtonId}
            onClick={handleOpenMenu}
            size="small"
            sx={{
              ...segmentHoverSx,
              width: 24,
              height: 24,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <ArrowDropDownDown
              fontSize="small"
              sx={{ color: iconColor, fontSize: 18 }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip placement="bottom" title={tooltipTitle}>
        <Box
          aria-checked={ariaChecked}
          aria-label={tooltipTitle}
          component="span"
          data-testid="select-all-batch-delete-checkbox"
          onClick={handleCheckboxClick}
          onKeyDown={handleCheckboxKeyDown}
          role="checkbox"
          sx={{
            ...segmentHoverSx,
            display: 'inline-flex',
            alignItems: 'center',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            cursor: disabled ? 'default' : 'pointer',
          }}
          tabIndex={disabled ? -1 : 0}
        >
          <Checkbox
            checked={selectionState === 'all'}
            disableRipple
            disabled={disabled}
            indeterminate={selectionState === 'some'}
            size="small"
            sx={{
              pointerEvents: 'none',
              color: isActive ? 'common.white' : 'primary.main',
              padding: '2px',
              '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                color: 'common.white',
              },
            }}
            tabIndex={-1}
          />
        </Box>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        id={menuId}
        onClose={handleCloseMenu}
        open={menuOpen}
        slotProps={{
          list: {
            'aria-labelledby': menuButtonId,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <MenuItem
          onClick={(): void => {
            onSelectAll();
            handleCloseMenu();
          }}
        >
          {allLabel}
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            onSelectNone();
            handleCloseMenu();
          }}
        >
          {noneLabel}
        </MenuItem>
      </Menu>
    </Box>
  );
};
